import { BusinessType } from '@/@types/generated'
import Loading from '@/components/Atoms/Loading'
import Paper from '@/components/Atoms/Paper'
import Address from '@/components/Molecules/Address'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Header } from '@/components/ui/header'
import format from '@/lib/format'
import { cn } from '@/lib/utils'
import { RobotIcon } from '@/components/Atoms/RobotIcon'
import { buildPath, Params, RouteKey } from '@/routes'
import {
  Building2Icon,
  ChevronLeftIcon,
  InfoIcon,
  TruckIcon,
} from 'lucide-react'
import { Suspense, useCallback } from 'react'
import {
  Link,
  Navigate,
  Outlet,
  useMatch,
  useNavigate,
  useParams,
} from 'react-router'
import {
  CompanyOperationDetails,
  GroupLabel,
} from '../components/CompanyOperationDetails'
import {
  TruckStopCompanyProvider,
  useTruckStopCompany as useCompany,
} from '../hooks/truckstop-company-provider'

const TabValues = {
  Certificate: 'certificate',
  AutomatedMotorVehicles: 'automated-motor-vehicles',
} as const

function TruckStopCompanyDetailsContent({
  businessEntityId,
}: {
  businessEntityId: string
}) {
  const { company } = useCompany()
  const isAvTabActive = !!useMatch(
    '/truckstop/companies/:businessEntityId/automated-motor-vehicles' satisfies RouteKey
  )

  const activeTab = isAvTabActive
    ? TabValues.AutomatedMotorVehicles
    : TabValues.Certificate

  const hasBoth = company.certificateStatus && company.autonomousVehicleStatus

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Button variant="outline" asChild>
          <Link to={buildPath('/truckstop/search')}>
            <ChevronLeftIcon />
            Back
          </Link>
        </Button>
      </div>

      {/* Company details — always visible, regardless of tab */}
      <Paper variant="outlined" className="flex flex-col gap-6 p-4">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-center">
          <div className="flex items-start gap-4">
            <div className="bg-primary flex size-14 flex-none items-center justify-center rounded-2xl">
              <Building2Icon className="text-primary-foreground size-6" />
            </div>
            <div className="flex flex-col gap-1">
              <GroupLabel>Company</GroupLabel>
              <Header variant="h4">{company.companyName}</Header>
              {/* TRK-008: a DBA name shows under the legal name, labelled */}
              {company.dbaName && (
                <span className="text-sm">DBA: {company.dbaName}</span>
              )}
              <div className="flex gap-2 [&>:not(:first-child)::before]:mr-2 [&>:not(:first-child)::before]:content-['•']">
                {company.businessType && (
                  <span className="text-muted-foreground text-sm">
                    {format.Enum(BusinessType, company.businessType)}
                  </span>
                )}
                {company.usdotNumber && (
                  <span className="text-muted-foreground text-sm">
                    USDOT{' '}
                    <span className="tabular-nums">{company.usdotNumber}</span>
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-x-8 gap-y-4 text-sm sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <GroupLabel>Physical Address</GroupLabel>
              <Address address={company.physicalAddress} />
            </div>
            <div className="flex flex-col gap-1">
              <GroupLabel>Mailing Address</GroupLabel>
              <Address address={company.mailingAddress} />
            </div>
          </div>

          <Button asChild>
            <Link
              to={{
                pathname: buildPath('/truckstop/complaints/file'),
                search: `?businessEntity=${businessEntityId}`,
              }}
            >
              File a Complaint
            </Link>
          </Button>
        </div>

        <div className="md:pl-18">
          {company.operationDetails ? (
            <CompanyOperationDetails
              operationDetails={company.operationDetails}
            />
          ) : (
            // TRK-009: with no SAFER record, a notice replaces the operation details
            <Alert variant="info" role="status">
              <InfoIcon />
              <AlertTitle>No Federal DOT record</AlertTitle>
              <AlertDescription>
                There is no Federal DOT record connected with this company.
              </AlertDescription>
            </Alert>
          )}
        </div>
      </Paper>

      {/* Split button switcher; only shown if both certificate and autonomous vehicle statuses are present */}
      {hasBoth && (
        <div className="grid w-sm grid-cols-2 gap-4 self-center md:w-xl">
          <Button
            variant={
              activeTab === TabValues.Certificate ? 'default' : 'outline'
            }
            size="big"
            className="flex flex-col items-center gap-2"
            asChild
          >
            <Link
              to={buildPath(
                '/truckstop/companies/:businessEntityId/certificate',
                { businessEntityId }
              )}
            >
              <TruckIcon className="size-12" />
              <span className="text-lg font-semibold">Certificate</span>
            </Link>
          </Button>

          <Button
            variant={
              activeTab === TabValues.AutomatedMotorVehicles
                ? 'default'
                : 'outline'
            }
            size="big"
            className={cn(
              'flex flex-col items-center gap-2',
              activeTab !== TabValues.AutomatedMotorVehicles &&
                'hover:[&>svg]:fill-primary'
            )}
            asChild
          >
            <Link
              to={buildPath(
                '/truckstop/companies/:businessEntityId/automated-motor-vehicles',
                { businessEntityId }
              )}
            >
              <RobotIcon
                className={cn(
                  'size-12',
                  activeTab === TabValues.AutomatedMotorVehicles &&
                    'fill-primary-foreground'
                )}
              />
              <span className="text-center text-lg font-semibold">
                Automated Motor <br className="md:hidden" />
                Vehicles
              </span>
            </Link>
          </Button>
        </div>
      )}

      <Suspense fallback={<Loading variant="fullscreen" />}>
        <Outlet />
      </Suspense>
    </div>
  )
}

export default function TruckStopCompanyDetails() {
  // Hooks
  const { businessEntityId } =
    useParams<Params<'/truckstop/companies/:businessEntityId'>>()
  const navigate = useNavigate()

  const handleError = useCallback(
    () => navigate(buildPath('/truckstop/search')),
    [navigate]
  )

  if (!businessEntityId) return <Navigate to={buildPath('/truckstop')} />

  return (
    <TruckStopCompanyProvider id={businessEntityId} onError={handleError}>
      <TruckStopCompanyDetailsContent businessEntityId={businessEntityId} />
    </TruckStopCompanyProvider>
  )
}
